import { type JSX, type ReactNode, useMemo, useState } from "react";
import styled from "styled-components";
import { currentColors } from "../colors";
import t from "../translations";

type Column<T> = {
    key: string;
    label: string;
    render: (row: T) => ReactNode;
    align?: "left" | "right" | "center";
};

interface IProps<T> {
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T) => string | number;
    highlightRow?: (row: T) => boolean;
    showMore?: boolean;
    initialLimit?: number;
    incrementBy?: number;
    headerColor?: string;
    rowAltColor?: string;
}

const StyledTable = styled.table<{ $headerColor: string; $rowAltColor: string }>`
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.5rem;
    border: 1px solid ${(props) => props.$headerColor};
    margin-bottom: 0;

    th {
        background-color: ${(props) => props.$headerColor};
        color: white;
        text-align: left;
    }

    tbody tr:nth-child(even) {
        background-color: ${(props) => props.$rowAltColor};
    }
`;

const HighlightRow = styled.tr`
    font-weight: bold;
`;

const EllipsisRow = styled.tr`
    text-align: center;
    font-style: italic;
    color: ${currentColors.text};
`;

const ShowMoreRow = styled.tr`
    td {
        text-align: center;
    }
`;

const ShowMoreButton = styled.button`
    margin: 0;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    background-color: ${currentColors.button};
    color: white;
    border: none;
    cursor: pointer;
    display: inline-block;
`;

export default function PerformanceTable<T>({
    columns,
    rows,
    rowKey,
    highlightRow,
    showMore = false,
    initialLimit = 10,
    incrementBy = 25,
    headerColor = currentColors.performance,
    rowAltColor = currentColors.performanceBackground,
}: IProps<T>): JSX.Element {
    const [displayLimit, setDisplayLimit] = useState(initialLimit);
    const highlightIndex = useMemo(() => {
        if (!highlightRow) {
            return -1;
        }
        return rows.findIndex((row) => highlightRow(row));
    }, [rows, highlightRow]);
    const highlightData = highlightIndex >= 0 ? rows[highlightIndex] : undefined;

    const visibleRows = showMore ? rows.slice(0, displayLimit) : rows;
    const highlightVisible = highlightIndex >= 0 && highlightIndex < visibleRows.length;
    const highlightOutside = highlightIndex >= 0 && !highlightVisible;

    let showEllipsis = false;
    let showMoreButton = false;
    const showHighlightedSeparately = highlightOutside;

    if (showMore) {
        if (highlightIndex >= 0) {
            if (highlightIndex === displayLimit) {
                showMoreButton = rows.length > displayLimit + 1;
            } else if (highlightIndex > displayLimit) {
                showEllipsis = true;
                showMoreButton = true;
            } else {
                showMoreButton = rows.length > displayLimit;
            }
        } else {
            showMoreButton = rows.length > displayLimit;
        }
    }

    const handleShowMore = () => {
        setDisplayLimit((currLimit) => {
            if (currLimit < incrementBy) {
                return incrementBy;
            }
            return currLimit + incrementBy;
        });
    };

    return (
        <StyledTable $headerColor={headerColor} $rowAltColor={rowAltColor}>
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.key} style={{ textAlign: column.align ?? "left" }}>
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {visibleRows.map((row) => {
                    const isHighlighted = highlightRow ? highlightRow(row) : false;
                    const RowComponent = isHighlighted ? HighlightRow : "tr";
                    return (
                        <RowComponent key={rowKey(row)}>
                            {columns.map((column) => (
                                <td key={column.key} style={{ textAlign: column.align ?? "left" }}>
                                    {column.render(row)}
                                </td>
                            ))}
                        </RowComponent>
                    );
                })}
                {showEllipsis && (
                    <EllipsisRow>
                        <td colSpan={columns.length}>{t("shared.performance-table.ellipsis")}</td>
                    </EllipsisRow>
                )}
                {showHighlightedSeparately && highlightData && (
                    <HighlightRow key={`highlight-${rowKey(highlightData)}`}>
                        {columns.map((column) => (
                            <td key={column.key} style={{ textAlign: column.align ?? "left" }}>
                                {column.render(highlightData)}
                            </td>
                        ))}
                    </HighlightRow>
                )}
                {showMoreButton && (
                    <ShowMoreRow>
                        <td colSpan={columns.length}>
                            <ShowMoreButton onClick={handleShowMore}>
                                {t("shared.performance-table.show-more-button")}
                            </ShowMoreButton>
                        </td>
                    </ShowMoreRow>
                )}
            </tbody>
        </StyledTable>
    );
}
